/**
 * Free-shipping threshold bar — currency conversion.
 *
 * The snippet renders every amount in EUR cents, because that is how Shopify stores the
 * "Offer free shipping · Minimum order amount" field on each shipping option. Shopify then
 * converts that minimum into the market's currency, so a hardcoded local number goes stale
 * as soon as the rate moves.
 *
 * This script does the same conversion the storefront already does, using the rate Shopify
 * publishes on every page. Where the market pays in the shop's own currency the rate is 1 and
 * the server-rendered markup is already final — we leave it alone, so the bar still works
 * with JavaScript disabled everywhere that matters most.
 *
 * data-rates-local is the hook for the opposite case: a destination whose rates the carrier
 * already quotes in the local currency, so converting them again would be wrong. It keeps the
 * RATES untouched and still converts the THRESHOLD. GB was the measured instance of this
 * (Reveni quotes £7.99–8.00, not converted euros) and is currently out of the table, so
 * nothing sets the attribute today — the support stays because the next such destination
 * needs it and the measurement behind it is recorded in the snippet.
 */
(function () {
  var SELECTOR = '[data-cart-shipping]';

  function conversionRate() {
    var rate = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate;
    rate = parseFloat(rate);
    return rate > 0 ? rate : 1;
  }

  function activeCurrency() {
    return (
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      (window.theme && window.theme.currency && window.theme.currency.active) ||
      'EUR'
    );
  }

  /**
   * Each currency is formatted in its own market's convention, not the page's language. The
   * storefront renders "258 kr" for a Swedish cart, so formatting with the page locale (English
   * here) would put "SEK 419.62" next to it. Only the converted currencies need an entry —
   * markets paying in the shop currency never reach this script.
   */
  var CURRENCY_LOCALE = {
    SEK: 'sv-SE',
    PLN: 'pl-PL',
    DKK: 'da-DK',
    CZK: 'cs-CZ',
    RON: 'ro-RO',
    GBP: 'en-GB',
    CHF: 'de-CH',
  };

  function money(cents) {
    var amount = cents / 100;
    var currency = activeCurrency();
    var locale =
      CURRENCY_LOCALE[currency] ||
      document.documentElement.lang ||
      (window.Shopify && window.Shopify.locale) ||
      'en';

    var formatted;
    try {
      formatted = new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
    } catch (error) {
      return amount.toFixed(2) + ' ' + currency;
    }

    // Match money_without_trailing_zeros, which the rest of the cart uses: "£21", not "£21.00".
    return formatted.replace(/([.,])00(?!\d)/, '');
  }

  function readCents(el, name) {
    var value = parseInt(el.getAttribute(name), 10);
    return isNaN(value) ? 0 : value;
  }

  function renderRates(el, rate) {
    var container = el.querySelector('[data-cart-shipping-rates]');
    if (!container) return;

    // Rates already quoted in the local currency must not be converted a second time.
    var ratesRate = el.getAttribute('data-rates-local') === 'true' ? 1 : rate;

    var parts = [
      { cents: readCents(el, 'data-standard'), label: el.getAttribute('data-label-standard') },
      { cents: readCents(el, 'data-pudo'), label: el.getAttribute('data-label-pudo') },
      { cents: readCents(el, 'data-express'), label: el.getAttribute('data-label-express') },
    ];

    var html = '';
    parts.forEach(function (part) {
      if (part.cents <= 0) return;
      var span = document.createElement('span');
      span.className = 'cart-shipping__rate';
      span.textContent = part.label + ': ' + money(Math.round(part.cents * ratesRate));
      html += span.outerHTML;
    });

    container.innerHTML = html;
  }

  function apply(el) {
    var rate = conversionRate();

    // Rate 1 means no conversion: the server already rendered the final numbers.
    if (rate === 1) {
      el.classList.remove('cart-shipping--pending');
      return;
    }

    var threshold = Math.round(readCents(el, 'data-threshold') * rate);
    if (threshold <= 0) return;

    var total = readCents(el, 'data-cart-total');
    var remaining = threshold - total;

    var percent = total > 0 ? Math.floor((total * 100) / threshold) : 0;
    if (percent > 100) percent = 100;

    var headline = el.querySelector('[data-cart-shipping-headline]');
    if (headline) {
      if (remaining > 0) {
        var template = el.getAttribute('data-message-away') || '';
        headline.textContent = template.replace('__AMOUNT__', money(remaining));
      } else {
        headline.textContent = el.getAttribute('data-message-qualified') || '';
      }
    }

    var track = el.querySelector('[data-cart-shipping-track]');
    if (track) track.setAttribute('aria-valuenow', String(percent));

    var fill = el.querySelector('[data-cart-shipping-fill]');
    if (fill) fill.style.width = percent + '%';

    renderRates(el, rate);

    el.classList.remove('cart-shipping--pending');
  }

  function applyAll() {
    var nodes = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) apply(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyAll);
  } else {
    applyAll();
  }

  /**
   * The cart drawer and cart page replace their markup wholesale on every change, and the block
   * that arrives is a fresh, still-unconverted one. Watching the DOM is what makes this reliable:
   * subscribing to CART_UPDATE alone is not enough, because whoever publishes it — the quantity
   * stepper, the discount form, the cross-sell widget, each with its own flow — may do so either
   * side of the re-render. A node arriving is the one signal that is always in the right order.
   */
  if (typeof MutationObserver === 'function') {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(SELECTOR)) {
            apply(node);
          } else if (node.querySelector && node.querySelector(SELECTOR)) {
            applyAll();
          }
        }
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  // Kept as a belt-and-braces signal for anything that mutates the cart without replacing markup.
  if (window.theme && theme.pubsub && theme.pubsub.events && theme.pubsub.events.CART_UPDATE) {
    theme.pubsub.subscribe(theme.pubsub.events.CART_UPDATE, function () {
      setTimeout(applyAll, 0);
    });
  }
})();
